import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Archive, ArchiveRestore, BarChart3, BellRing, Calendar, CalendarClock, CheckCircle, Clock, Download, Eye, FileDown, LogOut, Mail, MessageCircle, Phone, Search, StickyNote, Trash2 } from 'lucide-react';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { ADMIN_TOKEN_KEY as TOKEN_KEY } from '@/utils/adminAuth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { usePageMeta } from '@/hooks/usePageMeta';
import { projectsData } from '@/data/projectsData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const filters = ['all', 'new', 'contacted', 'closed'];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('active');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [scheduleDrafts, setScheduleDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [savingScheduleId, setSavingScheduleId] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [runningReminders, setRunningReminders] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  usePageMeta({
    title: 'Mandate Operations | Next Leap Inc',
    description: 'Private operational dashboard for Next Leap Inc customer inquiries.',
    robots: 'noindex,nofollow',
  });

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    instance.interceptors.response.use((response) => response, (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error('Session expired. Please sign in again.');
      }
      return Promise.reject(error);
    });
    return instance;
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setSubmissions([]);
    setStats(null);
    setAnalytics(null);
  };

  useEffect(() => {
    if (!token) return;
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const archived = viewMode === 'archived';
        const url = `/contact/submissions?archived=${archived}${filter === 'all' ? '' : `&status=${filter}`}`;
        const [submissionResponse, statsResponse, analyticsResponse] = await Promise.all([
          api.get(url),
          api.get('/contact/stats'),
          api.get('/analytics/summary'),
        ]);
        setSubmissions(submissionResponse.data);
        setStats(statsResponse.data);
        setAnalytics(analyticsResponse.data);
      } catch (error) {
        if (error.response?.status !== 401) toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, [filter, viewMode, token, api, reloadKey]);

  const sendReminder = async (id) => {
    setRemindingId(id);
    try {
      const response = await api.post(`/contact/submissions/${id}/reminder`);
      replaceSubmission(response.data);
      toast.success('Reminder email sent to the customer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const { data } = await api.post('/contact/reminders/run');
      toast.success(`Reminder sweep done: ${data.sent} sent, ${data.skipped} not due yet${data.failed ? `, ${data.failed} failed` : ''}`);
      if (data.sent) setReloadKey((key) => key + 1);
    } catch (error) {
      toast.error('Could not run reminder sweep');
    } finally {
      setRunningReminders(false);
    }
  };

  const downloadCalendar = async (submission) => {
    try {
      const response = await api.get(`/contact/submissions/${submission.id}/calendar.ics`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = `${(submission.project_interest || 'site-visit').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-site-visit.ics`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      toast.error('Could not download calendar invite');
    }
  };

  const visibleSubmissions = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return submissions;
    return submissions.filter((item) => {
      const notes = (item.notes || []).map((note) => note.text).join(' ');
      return `${item.name} ${item.email} ${item.phone} ${item.project_interest || ''} ${item.inquiry_type || ''} ${item.preferred_visit_date || ''} ${item.preferred_visit_time || ''} ${item.message} ${notes}`.toLowerCase().includes(search);
    });
  }, [query, submissions]);

  const replaceSubmission = (updated) => {
    setSubmissions((current) => current.map((item) => item.id === updated.id ? updated : item));
  };

  const refreshStats = async () => {
    const [statsResponse, analyticsResponse] = await Promise.all([api.get('/contact/stats'), api.get('/analytics/summary')]);
    setStats(statsResponse.data);
    setAnalytics(analyticsResponse.data);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/contact/submissions/${id}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      setSubmissions((current) => current.map((item) => item.id === id ? { ...item, status } : item));
      await refreshStats();
    } catch (error) {
      toast.error('Could not update status');
    }
  };

  const setArchived = async (id, archived) => {
    try {
      await api.patch(`/contact/submissions/${id}/archive`, { archived });
      toast.success(archived ? 'Inquiry archived' : 'Inquiry restored');
      setSubmissions((current) => current.filter((item) => item.id !== id));
      await refreshStats();
    } catch (error) {
      toast.error('Could not update archive state');
    }
  };

  const deletePermanently = async (id) => {
    if (!window.confirm('Permanently delete this archived inquiry? This cannot be undone.')) return;
    try {
      await api.delete(`/contact/submissions/${id}`);
      toast.success('Inquiry permanently deleted');
      setSubmissions((current) => current.filter((item) => item.id !== id));
      await refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not delete inquiry');
    }
  };

  const confirmVisit = async (id) => {
    setConfirmingId(id);
    try {
      const response = await api.patch(`/contact/submissions/${id}/visit-confirmation`);
      replaceSubmission(response.data);
      toast.success('Visit confirmed. Calendar invite is ready.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not confirm visit');
    } finally {
      setConfirmingId(null);
    }
  };

  const getScheduleDraft = (submission) => scheduleDrafts[submission.id] || {
    date: submission.preferred_visit_date || '',
    time: submission.preferred_visit_time || '',
  };

  const updateScheduleDraft = (id, field, value) => {
    setScheduleDrafts((current) => ({ ...current, [id]: { ...getScheduleDraft(submissions.find((item) => item.id === id) || {}), [field]: value } }));
  };

  const saveSchedule = async (submission) => {
    const draft = getScheduleDraft(submission);
    if (!draft.date || !draft.time) {
      toast.error('Choose both date and time');
      return;
    }
    setSavingScheduleId(submission.id);
    try {
      const response = await api.patch(`/contact/submissions/${submission.id}/visit-schedule`, {
        preferred_visit_date: draft.date,
        preferred_visit_time: draft.time,
      });
      replaceSubmission(response.data);
      toast.success('Visit slot updated. Confirmation has been reset.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update visit slot');
    } finally {
      setSavingScheduleId(null);
    }
  };

  const addNote = async (id) => {
    const text = (noteDrafts[id] || '').trim();
    if (!text) {
      toast.error('Write a note before saving');
      return;
    }
    setSavingNoteId(id);
    try {
      const response = await api.post(`/contact/submissions/${id}/notes`, { text });
      replaceSubmission(response.data);
      setNoteDrafts((current) => ({ ...current, [id]: '' }));
      toast.success('Private note added');
    } catch (error) {
      toast.error('Could not save note');
    } finally {
      setSavingNoteId(null);
    }
  };

  const deleteNote = async (submissionId, noteId) => {
    try {
      const response = await api.delete(`/contact/submissions/${submissionId}/notes/${noteId}`);
      replaceSubmission(response.data);
      toast.success('Note removed');
    } catch (error) {
      toast.error('Could not remove note');
    }
  };

  const exportCsv = () => {
    const rows = [['Name', 'Email', 'Phone', 'Project', 'Type', 'Visit Date', 'Visit Time', 'Visit Confirmed', 'Archived', 'Status', 'Notes', 'Message', 'Submitted At'], ...visibleSubmissions.map((item) => [item.name, item.email, item.phone, item.project_interest || '', item.inquiry_type || 'general', item.preferred_visit_date || '', item.preferred_visit_time || '', item.visit_confirmed ? 'yes' : 'no', item.archived ? 'yes' : 'no', item.status, (item.notes || []).map((note) => note.text).join(' | '), item.message, item.submitted_at])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = `next-leap-${viewMode}-inquiries.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const projectName = (projectId) => projectsData.find((project) => project.id === projectId)?.name || projectId;

  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <main className="min-h-screen bg-[#F5F5F5] pt-32" data-testid="admin-dashboard">
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }} className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div><div className="mb-4 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Mandate operations</div><h1 className="font-display text-4xl font-semibold tracking-tight text-[#0B1F3B] sm:text-5xl">Inquiry command center</h1><p className="mt-3 text-[#0B1F3B]/60">Restricted to authorised staff. Reminders go out automatically 24h before confirmed visits.</p></div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={exportCsv} className="h-12 rounded-full bg-[#0B1F3B] px-6 text-white hover:bg-[#06101E]" data-testid="admin-export-csv-btn"><Download className="h-4 w-4" />Export CSV</Button>
            <Button onClick={runReminders} disabled={runningReminders} variant="outline" className="h-12 rounded-full border-[#0B1F3B]/20 px-6 text-[#0B1F3B] hover:bg-[#EFE6D8]" data-testid="admin-run-reminders-btn"><BellRing className="h-4 w-4" />{runningReminders ? 'Checking...' : 'Send due reminders'}</Button>
            <ChangePasswordDialog api={api} />
            <Button onClick={logout} variant="outline" className="h-12 rounded-full border-[#0B1F3B]/20 px-6 text-[#0B1F3B] hover:bg-[#EFE6D8]" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Sign out</Button>
          </div>
        </motion.div>

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6" data-testid="admin-stats-grid">
          {[['Active', stats?.total_submissions], ['New', stats?.new], ['Contacted', stats?.contacted], ['Closed', stats?.closed], ['Archived', stats?.archived], ['7 days', stats?.last_7_days]].map(([label, value]) => <div key={label} className="rounded-[1.5rem] border border-[#0B1F3B]/10 bg-white p-5 shadow-sm" data-testid={`admin-stat-${label.toLowerCase().replace(' ', '-')}`}><div className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#0B1F3B]/45">{label}</div><div className="mt-3 font-display text-4xl font-semibold text-[#0B1F3B]">{value ?? '—'}</div></div>)}
        </div>

        <div className="mb-6 rounded-[1.7rem] border border-[#0B1F3B]/10 bg-[#0B1F3B] p-5 text-white shadow-sm" data-testid="admin-analytics-panel">
          <div className="mb-4 flex items-center gap-3"><BarChart3 className="h-5 w-5 text-[#C5A880]" /><h2 className="font-display text-2xl font-semibold">Private conversion analytics</h2></div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[['Project views', analytics?.project_view, 'analytics-card-project-views'], ['Brochures', analytics?.brochure_download, 'analytics-card-brochure-downloads'], ['WhatsApp', analytics?.whatsapp_click, 'analytics-card-whatsapp-clicks'], ['Visit requests', analytics?.site_visit_submit, 'analytics-card-site-visits'], ['Inquiries', analytics?.general_inquiry_submit, 'analytics-card-general-inquiries']].map(([label, value, testId]) => <div key={label} className="rounded-2xl bg-white/10 p-4" data-testid={testId}><div className="font-tech text-[10px] uppercase tracking-[0.18em] text-white/50">{label}</div><div className="mt-2 font-display text-3xl font-semibold">{value ?? '—'}</div></div>)}
          </div>
          {analytics?.top_projects?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{analytics.top_projects.slice(0, 4).map((project) => <span key={project.project_id} className="rounded-full border border-white/10 px-3 py-1 font-tech text-[10px] uppercase tracking-[0.14em] text-white/60" data-testid={`analytics-top-project-${project.project_id}`}>{projectName(project.project_id)} · {project.views} views</span>)}</div>}
        </div>

        <div className="mb-6 grid gap-4 rounded-[1.7rem] border border-[#0B1F3B]/10 bg-white p-4 shadow-sm xl:grid-cols-[1fr_auto_auto]" data-testid="admin-controls">
          <label className="relative block"><Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#0B1F3B]/40" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search name, phone, project, visit slot, note, or message" className="h-12 rounded-full border-[#0B1F3B]/10 bg-[#F5F5F5] pl-12" data-testid="admin-search-input" /></label>
          <div className="flex flex-wrap gap-2">{filters.map((item) => <button key={item} onClick={() => setFilter(item)} className={`rounded-full px-5 py-3 font-tech text-[11px] uppercase tracking-[0.16em] transition-colors ${filter === item ? 'bg-[#0B1F3B] text-white' : 'bg-[#F5F5F5] text-[#0B1F3B]/60 hover:bg-[#EFE6D8]'}`} data-testid={`admin-filter-${item}`}>{item}</button>)}</div>
          <div className="flex gap-2"><button onClick={() => setViewMode('active')} className={`rounded-full px-5 py-3 font-tech text-[11px] uppercase tracking-[0.16em] ${viewMode === 'active' ? 'bg-[#0B1F3B] text-white' : 'bg-[#F5F5F5] text-[#0B1F3B]/60'}`} data-testid="admin-view-active">Active</button><button onClick={() => setViewMode('archived')} className={`rounded-full px-5 py-3 font-tech text-[11px] uppercase tracking-[0.16em] ${viewMode === 'archived' ? 'bg-[#C5A880] text-[#0B1F3B]' : 'bg-[#F5F5F5] text-[#0B1F3B]/60'}`} data-testid="admin-view-archived">Archived</button></div>
        </div>

        {loading ? (
          <div className="rounded-[2rem] bg-white p-16 text-center shadow-sm" data-testid="admin-loading-state"><div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-[#0B1F3B]/15 border-t-[#0B1F3B]" />Loading dashboard</div>
        ) : visibleSubmissions.length === 0 ? (
          <div className="rounded-[2rem] border border-dashed border-[#0B1F3B]/20 bg-white p-16 text-center" data-testid="admin-empty-state"><Mail className="mx-auto mb-4 h-10 w-10 text-[#C5A880]" /><h2 className="font-display text-3xl font-semibold">No {viewMode} inquiries found.</h2><p className="mt-3 text-[#0B1F3B]/60">New submissions will appear here.</p></div>
        ) : (
          <div className="grid gap-4" data-testid="admin-inquiries-list">
            {visibleSubmissions.map((submission, index) => {
              const scheduleDraft = getScheduleDraft(submission);
              return (
              <motion.article key={submission.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.03 }} className="rounded-[1.7rem] border border-[#0B1F3B]/10 bg-white p-6 shadow-sm" data-testid={`admin-inquiry-row-${submission.id}`}>
                <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div><div className="flex flex-wrap items-center gap-3"><h2 className="font-display text-2xl font-semibold text-[#0B1F3B]">{submission.name}</h2><span className="rounded-full bg-[#EFE6D8] px-3 py-1 font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]">{submission.status}</span>{submission.inquiry_type === 'site_visit' && <span className="rounded-full bg-[#0B1F3B] px-3 py-1 font-tech text-[10px] uppercase tracking-[0.16em] text-white">Site visit</span>}{submission.visit_confirmed && <span className="rounded-full bg-[#1E7B54] px-3 py-1 font-tech text-[10px] uppercase tracking-[0.16em] text-white">Visit confirmed</span>}{submission.reminder_sent_at && <span className="rounded-full bg-[#C5A880] px-3 py-1 font-tech text-[10px] uppercase tracking-[0.16em] text-[#0B1F3B]" data-testid={`admin-reminder-sent-${submission.id}`}>Reminder sent</span>}{submission.archived && <span className="rounded-full bg-slate-200 px-3 py-1 font-tech text-[10px] uppercase tracking-[0.16em] text-slate-700">Archived</span>}</div><div className="mt-3 flex flex-wrap gap-4 text-sm text-[#0B1F3B]/65"><a href={`mailto:${submission.email}`} className="flex items-center gap-2 hover:text-[#0B1F3B]"><Mail className="h-4 w-4 text-[#C5A880]" />{submission.email}</a><a href={`tel:${submission.phone}`} className="flex items-center gap-2 hover:text-[#0B1F3B]"><Phone className="h-4 w-4 text-[#C5A880]" />{submission.phone}</a><span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-[#C5A880]" />{new Date(submission.submitted_at).toLocaleString('en-IN')}</span></div></div>
                  {submission.project_interest && <div className="rounded-full bg-[#0B1F3B] px-4 py-2 font-tech text-[10px] uppercase tracking-[0.16em] text-white">{submission.project_interest}</div>}
                </div>

                {(submission.preferred_visit_date || submission.preferred_visit_time) && <div className="mb-4 flex items-center gap-3 rounded-2xl border border-[#C5A880]/35 bg-[#EFE6D8]/55 p-4 text-sm font-semibold text-[#0B1F3B]" data-testid={`admin-visit-slot-${submission.id}`}><CalendarClock className="h-5 w-5 text-[#C5A880]" />Preferred visit: {submission.preferred_visit_date || 'Date flexible'} {submission.preferred_visit_time && `at ${submission.preferred_visit_time}`}</div>}
                <p className="rounded-2xl bg-[#F5F5F5] p-5 text-sm leading-relaxed text-[#0B1F3B]/70">{submission.message}</p>

                {viewMode === 'active' && submission.inquiry_type === 'site_visit' && !submission.visit_confirmed && (
                  <div className="mt-5 grid gap-3 rounded-2xl border border-[#0B1F3B]/10 bg-[#F5F5F5]/70 p-4 sm:grid-cols-[1fr_1fr_auto]" data-testid={`admin-reschedule-${submission.id}`}>
                    <Input type="date" value={scheduleDraft.date} onChange={(event) => updateScheduleDraft(submission.id, 'date', event.target.value)} className="h-11 rounded-xl bg-white" data-testid={`admin-visit-date-${submission.id}`} />
                    <Input type="time" value={scheduleDraft.time} onChange={(event) => updateScheduleDraft(submission.id, 'time', event.target.value)} className="h-11 rounded-xl bg-white" data-testid={`admin-visit-time-${submission.id}`} />
                    <Button onClick={() => saveSchedule(submission)} disabled={savingScheduleId === submission.id} className="rounded-xl bg-[#0B1F3B] text-white hover:bg-[#06101E]" data-testid={`save-visit-slot-${submission.id}`}>{savingScheduleId === submission.id ? 'Saving...' : 'Save slot'}</Button>
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button asChild size="sm" className="rounded-full bg-[#1E7B54] text-white hover:bg-[#166846]" data-testid={`admin-whatsapp-${submission.id}`}><a href={`https://wa.me/${String(submission.phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${submission.name}, this is Next Leap Inc regarding your ${submission.project_interest || 'real estate'} inquiry.`)}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" />WhatsApp</a></Button>
                  {viewMode === 'active' && submission.inquiry_type === 'site_visit' && !submission.visit_confirmed && <Button size="sm" onClick={() => confirmVisit(submission.id)} disabled={confirmingId === submission.id} className="rounded-full bg-[#C5A880] text-[#0B1F3B] hover:bg-[#EFE6D8]" data-testid={`confirm-visit-${submission.id}`}><CalendarClock className="h-4 w-4" />{confirmingId === submission.id ? 'Confirming...' : 'Confirm visit'}</Button>}
                  {submission.inquiry_type === 'site_visit' && submission.visit_confirmed && <Button size="sm" onClick={() => downloadCalendar(submission)} className="rounded-full bg-[#0B1F3B] text-white hover:bg-[#06101E]" data-testid={`download-calendar-${submission.id}`}><FileDown className="h-4 w-4" />Download invite</Button>}
                  {viewMode === 'active' && submission.inquiry_type === 'site_visit' && submission.visit_confirmed && <Button size="sm" onClick={() => sendReminder(submission.id)} disabled={remindingId === submission.id} className="rounded-full bg-[#EFE6D8] text-[#0B1F3B] hover:bg-[#C5A880]" data-testid={`send-reminder-${submission.id}`}><BellRing className="h-4 w-4" />{remindingId === submission.id ? 'Sending...' : submission.reminder_sent_at ? 'Resend reminder' : 'Send reminder'}</Button>}
                  {viewMode === 'active' && submission.status !== 'new' && <Button size="sm" onClick={() => updateStatus(submission.id, 'new')} className="rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100" data-testid={`mark-new-${submission.id}`}><Clock className="h-4 w-4" />New</Button>}
                  {viewMode === 'active' && submission.status !== 'contacted' && <Button size="sm" onClick={() => updateStatus(submission.id, 'contacted')} className="rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100" data-testid={`mark-contacted-${submission.id}`}><Eye className="h-4 w-4" />Contacted</Button>}
                  {viewMode === 'active' && submission.status !== 'closed' && <Button size="sm" onClick={() => updateStatus(submission.id, 'closed')} className="rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100" data-testid={`mark-closed-${submission.id}`}><CheckCircle className="h-4 w-4" />Closed</Button>}
                  {viewMode === 'active' ? <Button size="sm" onClick={() => setArchived(submission.id, true)} className="rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`archive-inquiry-${submission.id}`}><Archive className="h-4 w-4" />Archive</Button> : <><Button size="sm" onClick={() => setArchived(submission.id, false)} className="rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100" data-testid={`restore-inquiry-${submission.id}`}><ArchiveRestore className="h-4 w-4" />Restore</Button><Button size="sm" onClick={() => deletePermanently(submission.id)} className="rounded-full bg-red-50 text-red-700 hover:bg-red-100" data-testid={`delete-inquiry-${submission.id}`}><Trash2 className="h-4 w-4" />Delete permanently</Button></>}
                </div>

                <div className="mt-6 rounded-2xl border border-[#0B1F3B]/10 bg-[#F5F5F5]/70 p-4" data-testid={`admin-notes-${submission.id}`}>
                  <div className="mb-3 flex items-center gap-2 font-tech text-[11px] uppercase tracking-[0.18em] text-[#0B1F3B]/55"><StickyNote className="h-4 w-4 text-[#C5A880]" />Private follow-up notes</div>
                  <div className="grid gap-3">
                    {(submission.notes || []).map((note) => <div key={note.id} className="flex items-start justify-between gap-3 rounded-xl bg-white p-3 text-sm" data-testid={`admin-note-${note.id}`}><div><p className="text-[#0B1F3B]/75">{note.text}</p><p className="mt-1 font-tech text-[10px] uppercase tracking-[0.14em] text-[#0B1F3B]/40">{new Date(note.created_at).toLocaleString('en-IN')}</p></div><button onClick={() => deleteNote(submission.id, note.id)} className="rounded-full p-2 text-red-600 transition-colors hover:bg-red-50" aria-label="Delete note" data-testid={`admin-note-delete-${note.id}`}><Trash2 className="h-4 w-4" /></button></div>)}
                    <div className="grid gap-2 sm:grid-cols-[1fr_auto]"><Textarea value={noteDrafts[submission.id] || ''} onChange={(event) => setNoteDrafts((current) => ({ ...current, [submission.id]: event.target.value }))} rows={2} maxLength={1000} placeholder="Add call outcome, budget note, next action..." className="resize-none rounded-xl bg-white" data-testid={`admin-note-input-${submission.id}`} /><Button onClick={() => addNote(submission.id)} disabled={savingNoteId === submission.id} className="rounded-xl bg-[#0B1F3B] text-white hover:bg-[#06101E]" data-testid={`admin-note-add-${submission.id}`}>{savingNoteId === submission.id ? 'Saving...' : 'Add note'}</Button></div>
                  </div>
                </div>
              </motion.article>
            )})}
          </div>
        )}
      </section>
    </main>
  );
};

export default Admin;
